/**
 * The thermal receipt: one pure function that turns a sale into an HTML page exactly `dots` pixels wide.
 *
 * The same function runs in three places, so what you preview is what prints:
 *   - the Electron POS rasterises it (Chromium shapes the Arabic; the printer only receives dots, so no Arabic code page is involved),
 *   - the web dashboard shows it as the live preview in Settings,
 *   - tests render it to compare against golden images.
 * Fonts and the logo are injected (`ReceiptAssets`) because each runtime loads them differently.
 */
import { formatJod } from "./money.js";

export type ReceiptLanguage = "ui" | "ar" | "en";
export type ReceiptLocale = "ar" | "en";
export type ReceiptFontSize = "small" | "normal" | "large";
export type ReceiptItemNames = "language" | "both";

/** What a manager can change about the look of the receipt (stored per branch, optionally overridden per terminal). */
export interface ReceiptLayout {
  /** "ui" = the language the cashier is using; "ar" / "en" force Arabic (right-to-left) or English (left-to-right). */
  language: ReceiptLanguage;
  showLogo: boolean;
  /** Logo width as a percentage of the printable width (30–90). */
  logoWidthPercent: number;
  /** Address, phone and tax-number lines from the company details. */
  showHeaderLines: boolean;
  /** One extra line under the header (e.g. a slogan or Wi-Fi). */
  extraHeader: string;
  fontSize: ReceiptFontSize;
  /** "both" prints the Arabic and the English name of every item. */
  itemNames: ReceiptItemNames;
  showUnitPrice: boolean;
  showTaxLine: boolean;
  showPayments: boolean;
  showTerminal: boolean;
  /** Printed natively as CODE128 under the receipt (wristband / gate code). */
  showBarcode: boolean;
  showQr: boolean;
  qrText: string;
  /** Overrides the company footer when not empty. */
  footerAr: string;
  footerEn: string;
}

export const DEFAULT_RECEIPT_LAYOUT: ReceiptLayout = {
  language: "ui", showLogo: true, logoWidthPercent: 62, showHeaderLines: true, extraHeader: "", fontSize: "normal", itemNames: "language",
  showUnitPrice: false, showTaxLine: true, showPayments: true, showTerminal: true, showBarcode: true, showQr: false, qrText: "", footerAr: "", footerEn: "",
};

export const RECEIPT_LAYOUT_KEYS = Object.keys(DEFAULT_RECEIPT_LAYOUT) as Array<keyof ReceiptLayout>;

const clamp = (n: unknown, lo: number, hi: number, d: number) => (typeof n === "number" && Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : d);
const str = (v: unknown, max: number, d: string) => (typeof v === "string" ? v.trim().slice(0, max) : d);
const bool = (v: unknown, d: boolean) => (typeof v === "boolean" ? v : d);
const oneOf = <T extends string>(v: unknown, all: readonly T[], d: T): T => (all.includes(v as T) ? (v as T) : d);

/** Never throws: anything unknown or out of range falls back to the default, so a bad stored value can never stop a sale from printing. */
export function normalizeLayout(raw: unknown, base: ReceiptLayout = DEFAULT_RECEIPT_LAYOUT): ReceiptLayout {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    language: oneOf(r.language, ["ui", "ar", "en"], base.language),
    showLogo: bool(r.showLogo, base.showLogo),
    logoWidthPercent: clamp(r.logoWidthPercent, 30, 90, base.logoWidthPercent),
    showHeaderLines: bool(r.showHeaderLines, base.showHeaderLines),
    extraHeader: str(r.extraHeader, 120, base.extraHeader),
    fontSize: oneOf(r.fontSize, ["small", "normal", "large"], base.fontSize),
    itemNames: oneOf(r.itemNames, ["language", "both"], base.itemNames),
    showUnitPrice: bool(r.showUnitPrice, base.showUnitPrice),
    showTaxLine: bool(r.showTaxLine, base.showTaxLine),
    showPayments: bool(r.showPayments, base.showPayments),
    showTerminal: bool(r.showTerminal, base.showTerminal),
    showBarcode: bool(r.showBarcode, base.showBarcode),
    showQr: bool(r.showQr, base.showQr),
    qrText: str(r.qrText, 200, base.qrText),
    footerAr: str(r.footerAr, 160, base.footerAr),
    footerEn: str(r.footerEn, 160, base.footerEn),
  };
}

/** branch layer, then terminal layer: later layers only change the keys they actually contain. */
export function effectiveLayout(...layers: unknown[]): ReceiptLayout {
  return layers.reduce<ReceiptLayout>((acc, l) => normalizeLayout(l, acc), DEFAULT_RECEIPT_LAYOUT);
}

export const resolveReceiptLocale = (language: ReceiptLanguage, ui: ReceiptLocale): ReceiptLocale => (language === "ui" ? ui : language);

/** The sale as printed. Mirrors the payload sent to the Electron bridge. */
export interface ReceiptData {
  locale: ReceiptLocale;
  stationName: string;
  headerLines?: string[];
  footer?: string | null;
  receiptNo: string;
  createdAt: string;
  terminal: string | null;
  items: Array<{ description: string; descriptionAr: string | null; qty: number; unitPrice: number; lineTotal: number }>;
  subtotal: number;
  discount: number;
  discountLabel?: string | null;
  tax: number;
  total: number;
  payments: Array<{ method: string; amount: number }>;
  barcode?: string;
  layout?: ReceiptLayout;
}

export interface ReceiptAssets {
  /** @font-face rules for "Cairo" (Arabic + Latin). */
  fontCss: string;
  /** Data URI of the one-colour logo. */
  logoUri: string;
}

const TEXT = {
  ar: { receipt: "إيصال", subtotal: "المجموع الفرعي", discount: "الخصم", tax: "الضريبة", total: "الإجمالي", thanks: "شكراً لزيارتكم أسترو بلانيت", cur: "دينار", terminal: "نقطة البيع", cash: "نقداً", card: "بطاقة", cliq: "كليك", credit: "ذمم", free_stamp: "طوابع الولاء", wallet: "المحفظة" },
  en: { receipt: "Receipt", subtotal: "Subtotal", discount: "Discount", tax: "Tax", total: "Total", thanks: "Thank you for visiting AstroPlanet", cur: "JOD", terminal: "Terminal", cash: "Cash", card: "Card", cliq: "CliQ", credit: "Credit", free_stamp: "Loyalty stamps", wallet: "Wallet" },
} as const;

export const escapeHtml = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

function formatDateTime(iso: string, locale: ReceiptLocale): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-JO-u-nu-latn" : "en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Amman" }).format(d);
}

const SCALE: Record<ReceiptFontSize, number> = { small: 0.88, normal: 1, large: 1.14 };

/**
 * The receipt as a page exactly `dots` pixels wide, black on white at thermal-friendly weight.
 * Right-to-left for Arabic; numbers, times and codes are isolated left-to-right so they never reorder.
 */
export function thermalReceiptHtml(d: ReceiptData, dots: number, assets: ReceiptAssets): string {
  const L = d.layout ?? DEFAULT_RECEIPT_LAYOUT;
  const ar = d.locale === "ar";
  const tr = TEXT[d.locale] as Record<string, string>;
  const base = Math.round((dots >= 500 ? 25 : 21) * SCALE[L.fontSize]);
  const money = (n: number) => `<span class="n">${formatJod(n)} ${tr.cur}</span>`;
  const rows = d.items
    .map((i) => {
      const primary = ar && i.descriptionAr ? i.descriptionAr : i.description;
      const secondary = L.itemNames === "both" ? (ar ? i.description : i.descriptionAr) : null;
      const detail = L.showUnitPrice && i.qty > 1 ? `<div class="sm"><span class="n">${i.qty} × ${formatJod(i.unitPrice)}</span></div>` : "";
      return `<tr><td class="d">${escapeHtml(primary)}${i.qty > 1 ? ` <span class="n">×${i.qty}</span>` : ""}${secondary && secondary !== primary ? `<div class="sm">${escapeHtml(secondary)}</div>` : ""}${detail}</td><td class="r">${money(i.lineTotal)}</td></tr>`;
    })
    .join("");
  const pays = L.showPayments ? d.payments.map((p) => `<tr><td>${escapeHtml(tr[p.method] ?? p.method)}</td><td class="r">${money(p.amount)}</td></tr>`).join("") : "";
  const footer = (ar ? L.footerAr : L.footerEn) || (d.footer?.trim() ? d.footer : "") || tr.thanks || "";
  const logoW = Math.round(dots * (L.logoWidthPercent / 100));

  return `<!doctype html><html lang="${d.locale}" dir="${ar ? "rtl" : "ltr"}"><head><meta charset="utf-8"><style>
${assets.fontCss}
html,body{margin:0;padding:0;background:#fff;overflow:hidden}
body{width:${dots}px;box-sizing:border-box;padding:6px 8px 4px;font-family:"Cairo",sans-serif;font-size:${base}px;font-weight:700;line-height:1.35;color:#000}
.n{direction:ltr;unicode-bidi:isolate;display:inline-block}
.c{text-align:center}.r{text-align:${ar ? "left" : "right"};white-space:nowrap;padding-${ar ? "right" : "left"}:10px}
table{width:100%;border-collapse:collapse}td{padding:2px 0;vertical-align:top}.d{width:100%;overflow-wrap:anywhere}
hr{border:0;border-top:3px dashed #000;margin:8px 0}
.tot td{font-size:${base + 5}px;padding-top:4px}.sm{font-size:${base - 3}px;font-weight:400}
</style></head><body>
${L.showLogo ? `<div class="c"><img src="${assets.logoUri}" alt="${escapeHtml(d.stationName)}" style="width:${logoW}px;height:auto;display:block;margin:0 auto 4px"></div>` : `<div class="c" style="font-size:${base + 9}px">${escapeHtml(d.stationName)}</div>`}
${L.extraHeader ? `<div class="c sm">${escapeHtml(L.extraHeader)}</div>` : ""}
${L.showHeaderLines ? (d.headerLines ?? []).map((l) => `<div class="c sm">${escapeHtml(l)}</div>`).join("") : ""}
<div class="c">${tr.receipt} <span class="n">${escapeHtml(d.receiptNo)}</span></div>
<div class="c sm"><span class="n">${escapeHtml(formatDateTime(d.createdAt, d.locale))}</span>${L.showTerminal && d.terminal ? ` • ${tr.terminal} <span class="n">${escapeHtml(d.terminal)}</span>` : ""}</div>
<hr><table>${rows}</table><hr>
<table>
${d.discount || (d.tax && L.showTaxLine) ? `<tr><td>${tr.subtotal}</td><td class="r">${money(d.subtotal)}</td></tr>` : ""}
${d.discount ? `<tr><td>${tr.discount}${d.discountLabel ? ` (${escapeHtml(d.discountLabel)})` : ""}</td><td class="r">${money(-d.discount)}</td></tr>` : ""}
${d.tax && L.showTaxLine ? `<tr><td>${tr.tax}</td><td class="r">${money(d.tax)}</td></tr>` : ""}
<tr class="tot"><td>${tr.total}</td><td class="r">${money(d.total)}</td></tr>${pays}
</table><hr>
<div class="c">${escapeHtml(footer)}</div>
</body></html>`;
}

/** A realistic sale with long Arabic names, a coupon and several payment methods: used by the live preview and the test page. */
export function sampleReceipt(locale: ReceiptLocale, over: Partial<ReceiptData> = {}): ReceiptData {
  return {
    locale,
    stationName: locale === "ar" ? "أسترو بلانيت للأطفال" : "AstroPlanet For Kids",
    headerLines: locale === "ar" ? ["عمّان — الدوار السابع", "هاتف: 0790000000", "الرقم الضريبي: 123456"] : ["Amman — 7th Circle", "Phone: 0790000000", "Tax No.: 123456"],
    footer: null,
    receiptNo: "R-1042",
    createdAt: new Date().toISOString(),
    terminal: "POS-1",
    items: [
      { description: "1 Hour Orbit Pass", descriptionAr: "تذكرة مدارية لمدة ساعة", qty: 2, unitPrice: 5000, lineTotal: 10000 },
      { description: "Astro-Grip Safety Socks (M)", descriptionAr: "جوارب الأمان أسترو-غريب (متوسط)", qty: 2, unitPrice: 1000, lineTotal: 2000 },
      { description: "Fresh Orange Juice", descriptionAr: "عصير برتقال طازج", qty: 1, unitPrice: 1750, lineTotal: 1750 },
    ],
    subtotal: 13750,
    discount: 1375,
    discountLabel: locale === "ar" ? "خصم الإخوة" : "Siblings discount",
    tax: 1980,
    total: 14355,
    payments: [{ method: "cash", amount: 10000 }, { method: "card", amount: 4355 }],
    barcode: "WB-104",
    ...over,
  };
}
